import React, { Component, ReactNode } from "react";
import { difference, sortBy, union } from "lodash";
import {
  cellsToSpans,
  flattenSpan,
  flattenSpans,
  flipEnds,
  isCellInSpans,
  Span,
  RenderStyle,
  PeriodColors
} from "./utils";
import { RowSpanGrid } from "./RowSpanGrid";

type Props<T> = {
  cells: T[];
  disabledCellsGroupIndexes?: number[];
  renderStyle: RenderStyle;
  contentSelector: (v: T) => ReactNode;
  renderPrefix?: (v: {
    current: T;
    previous: T;
    next?: T;
    background: string;
  }) => ReactNode;

  renderCellContent?: (index: number, color: string) => string;
  isCellDisabled?: (index: number, color: string) => boolean;
  skipDisabledSpans?: boolean;
  onSelect?: (span: Span[]) => void;
  onClick?: (span: Span) => void;
  onHover?: (cellNumber: number, e: React.MouseEvent) => void;
  value?: Array<{ start: number; end: number; color?: string }>;
  spans: Span[];
  readonly?: boolean;
};

type State = {
  spans: Span[];
  selection: Span | null;
  isDeselecting: boolean;
};

export class MultiInteractiveSpanGrid<T> extends Component<Props<T>, State> {
  static defaultProps: Partial<Props<any>> = {
    disabledCellsGroupIndexes: [],
    isCellDisabled: () => false
  };

  static getDerivedStateFromProps(props: Props<any>, state: State): State {
    return {
      ...state,
      spans: props.value
        ? props.value.map((x) => createSpan(true, x.start, x.end))
        : state.spans
    };
  }

  state: State = {
    spans: [],
    selection: null,
    isDeselecting: false
  };

  componentWillUnmount() {
    document.body.removeEventListener("mouseup", this.onMouseUp);
  }

  handleMouseDown = (event: React.MouseEvent) => {
    const cell = eventToCell(event);

    if (typeof cell !== "number" || cell < 0) {
      return;
    }

    if (this.props.disabledCellsGroupIndexes.includes(cell)) {
      return;
    }

    const isDeselecting = isCellInSpans(cell, this.state.spans);

    this.setState({
      selection: createSpan(!isDeselecting, cell, cell),
      isDeselecting
    });

    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
  };

  handleMouseOver = (event: React.MouseEvent) => {
    const cell = eventToCell(event);

    if (typeof cell !== "number" || cell < 0) {
      return;
    }

    this.props.onHover?.(cell, event);
  };

  onMouseMove = (event: any) => {
    const cell = eventToCell(event);

    if (typeof cell !== "number" || cell < 0) {
      return;
    }

    this.setState({
      selection: createSpan(!this.state.isDeselecting, this.state.selection.start, cell)
    });
  };

  onMouseUp = () => {
    const {
      disabledCellsGroupIndexes,
      cells: { length },
      onClick,
      onSelect
    } = this.props;

    const { selection: _selection, spans, isDeselecting } = this.state;

    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);

    if (!_selection) {
      return;
    }

    const selection = flipEnds(_selection);
    const prevSelectedCells = flattenSpans(spans);
    const selectedCells = flattenSpan(selection);

    const result = isDeselecting
      ? difference(prevSelectedCells, selectedCells)
      : union(prevSelectedCells, selectedCells);

    const resultWithoutDisabled = result.filter((x) =>
      this.props.isCellDisabled
        ? !this.props.isCellDisabled(x, PeriodColors.Selected)
        : true
    );

    const resultSorted = sortBy(resultWithoutDisabled);

    const updatedSpans = cellsToSpans(
      resultSorted,
      length,
      createSpanCb,
      disabledCellsGroupIndexes
    );

    if (!this.props.skipDisabledSpans) {
      const disabledSpans = cellsToSpans(disabledCellsGroupIndexes, length, createSpanCb);
      updatedSpans.push(...disabledSpans);
    }

    if (selection.start === selection.end && onClick) {
      onClick(selection);
    }
    this.setState({ spans: updatedSpans, selection: null }, () => {
      onSelect && onSelect(updatedSpans);
    });
  };

  render() {
    const { selection, spans } = this.state;

    return (
      <RowSpanGrid<T>
        isCellDisabled={(index, date, color) => {
          return (
            this.props.disabledCellsGroupIndexes.includes(index) ||
            this.props.isCellDisabled?.(index, color)
          );
        }}
        renderCellContent={this.props.renderCellContent}
        readonly={this.props.readonly}
        renderPrefix={this.props.renderPrefix}
        renderStyle={this.props.renderStyle}
        cells={this.props.cells}
        spans={[
          ...this.props.spans,
          ...spans,
          selection ? flipEnds(selection) : null
        ].filter((x) => x)}
        contentSelector={this.props.contentSelector}
        onMouseDown={this.handleMouseDown}
        onMouseOver={this.handleMouseOver}
      />
    );
  }
}

function eventToCell(event) {
  const _cell = event.target.closest("div[data-cell]");

  if (!_cell) {
    return;
  }

  return parseInt(_cell.dataset.cell);
}

function createSpan(selected: boolean, start: number, end: number): Span {
  return {
    color: selected ? PeriodColors.Selected : PeriodColors.None,
    colorMix: 0.35,
    start,
    end
  };
}

function createSpanCb(start, end) {
  return { start, end, color: PeriodColors.Selected, colorMix: 0 };
}
